import React, { Suspense, lazy } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./lib/scrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import SEO from './components/SEO';
import Loader from './components/Loader';
import { generateLocalBusinessSchema } from "./utils/structuredData";

// Lazy load pages
const Index = lazy(() => import("./pages/Index"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const CaseStudies = lazy(() => import("./pages/RecentWorkDetails"));
const BlogListing = lazy(() => import("./pages/BlogListing"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RecentWorks = lazy(() => import("./pages/RecentWorks"));
const WhatWeDo = lazy(() => import("./pages/WhatWeDo"));
const HowWeDo = lazy(() => import("./pages/HowWeDo"));
const Careers = lazy(() => import("./pages/Careers"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Applications = lazy(() => import("./pages/admin/Applications"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const Newsletter = lazy(() => import("./pages/admin/Newsletter"));
const Leads = lazy(() => import("./pages/admin/Leads"));
const JobOpenings = lazy(() => import("./pages/admin/JobOpenings"));
const RecentWorkDetails = lazy(() => import("./pages/RecentWorkDetails"));

// Create a client
const queryClient = new QueryClient();

const App: React.FC = () => {
  const localBusinessSchema = generateLocalBusinessSchema();
  return (
    <React.StrictMode>
      <AuthProvider>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<Loader />}>
                  <Routes>
                    <Route path="/" element={<><SEO /><Index /></>} />
                    <Route path="/about-us" element={<><SEO title="About Us" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'About Us', url: 'https://regaloutsourceindia.com/about-us' }]} /><AboutUs /></>} />
                    <Route path="/recent-works/:id" element={<><SEO title="Recent Works" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'Recent Works', url: 'https://regaloutsourceindia.com/recent-works' }]} /><RecentWorkDetails /></>} />
                    <Route path="/blog" element={<><SEO title="Blog" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'Blog', url: 'https://regaloutsourceindia.com/blog' }]} /><BlogListing /></>} />
                    <Route path="/blog/:slug" element={<><SEO type="article" /><BlogPost /></>} />
                    <Route path="/contact" element={<><SEO title="Contact Us" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'Contact', url: 'https://regaloutsourceindia.com/contact' }]} structuredData={localBusinessSchema} /><Contact /></>} />
                    <Route path="/recent-works" element={<><SEO title="Recent Works" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'Recent Works', url: 'https://regaloutsourceindia.com/recent-works' }]} /><RecentWorks /></>} />
                    <Route path="/what-we-do" element={<><SEO title="What We Do" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'What We Do', url: 'https://regaloutsourceindia.com/what-we-do' }]} /><WhatWeDo /></>} />
                    <Route path="/how-we-do" element={<><SEO title="How We Do It" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'How We Do It', url: 'https://regaloutsourceindia.com/how-we-do' }]} /><HowWeDo /></>} />
                    <Route path="/careers" element={<><SEO title="Careers" breadcrumbs={[{ name: 'Home', url: 'https://regaloutsourceindia.com/' }, { name: 'Careers', url: 'https://regaloutsourceindia.com/careers' }]} /><Careers /></>} />
                    <Route path="/privacy-policy" element={<><SEO title="Privacy Policy" noindex={true} /><PrivacyPolicy /></>} />
                    <Route path="/terms" element={<><SEO title="Terms of Service" noindex={true} /><TermsOfService /></>} />
                    <Route path="/admin/login" element={<><SEO title="Admin Login" noindex={true} /><AdminLogin /></>} />
                    <Route path="/admin/dashboard" element={<><SEO title="Dashboard" noindex={true} /><Dashboard /></>} />
                    <Route path="/admin/applications" element={<><SEO title="Applications" noindex={true} /><Applications /></>} />
                    <Route path="/admin/blogs" element={<><SEO title="Blog Management" noindex={true} /><BlogManagement /></>} />
                    <Route path="/admin/newsletter" element={<><SEO title="Newsletter" noindex={true} /><Newsletter /></>} />
                    <Route path="/admin/leads" element={<><SEO title="Leads" noindex={true} /><Leads /></>} />
                    <Route path="/admin/job-openings" element={<><SEO title="Job Openings" noindex={true} /><JobOpenings /></>} />
                    {/* Add more routes as needed */}
                    <Route path="/404" element={<><SEO title="Page Not Found" noindex={true} /><NotFound /></>} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </AuthProvider>
    </React.StrictMode>
  );
};

export default App;
